import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn';
import type { Size } from '../../lib/types';

type SpinnerSize = Extract<Size, 'xs' | 'sm' | 'md' | 'lg'>;

export interface SpinnerProps extends React.OutputHTMLAttributes<HTMLOutputElement> {
  /** @default 'md' */
  size?: SpinnerSize;

  /**
   * Read by screen readers.
   * @default 'Loading'
   */
  label?: string;

  className?: string;

  ref?: React.Ref<HTMLOutputElement>;
}

const sizeClasses = {
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
} satisfies Record<SpinnerSize, string>;

/** Renders `<output>` for its implicit `role="status"`. Respects `prefers-reduced-motion`. */
export function Spinner({
  size = 'md',
  label = 'Loading',
  className,
  ref,
  ...props
}: SpinnerProps) {
  return (
    <output
      ref={ref}
      aria-label={label}
      data-size={size}
      className={cn(
        'inline-flex items-center justify-center',
        'text-muted-foreground',
        sizeClasses[size],
        className,
      )}
      {...props}
    >
      <Loader2
        className={cn(
          'size-full animate-spin',
          'motion-reduce:animate-none motion-reduce:opacity-70',
        )}
        aria-hidden="true"
      />
      <span className="sr-only">{label}</span>
    </output>
  );
}
